import {
  MAX_TEXT_LENGTH,
  parseAnyText,
  toPixelwerkstattError,
  type PixelDocument,
} from "../core/index.js";

export interface ImportModalCallbacks {
  readonly onImportSuccess: (doc: PixelDocument) => void;
  readonly isDocumentDirty: () => boolean;
}

export class ImportModal {
  private overlay: HTMLElement | null = null;
  private readonly callbacks: ImportModalCallbacks;

  constructor(callbacks: ImportModalCallbacks) {
    this.callbacks = callbacks;
  }

  public open(): void {
    if (this.overlay) return;

    this.overlay = document.createElement("div");
    this.overlay.className = "modal-backdrop";
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-labelledby", "modal-import-title");

    const modal = document.createElement("div");
    modal.className = "modal-window";

    const header = document.createElement("div");
    header.className = "modal-header";
    header.innerHTML = `
      <h2 id="modal-import-title" class="modal-title">Raster / Projekt importieren</h2>
      <button type="button" class="modal-close" aria-label="Schließen">×</button>
    `;

    const body = document.createElement("div");
    body.className = "modal-body";

    // File selection box
    const fileGroup = document.createElement("div");
    fileGroup.className = "form-group";
    fileGroup.innerHTML = `
      <label class="field-label">Datei auswählen (.json oder .txt)</label>
      <div class="file-dropzone">
        <input type="file" class="file-input" accept=".json,.txt" />
        <div class="dropzone-label">Hier klicken oder Datei ablegen</div>
      </div>
    `;

    // Textarea for pasting
    const textGroup = document.createElement("div");
    textGroup.className = "form-group";
    textGroup.innerHTML = `
      <label for="import-textarea" class="field-label">Oder Hex-Raster / JSON direkt einfügen</label>
      <textarea id="import-textarea" class="modal-textarea" rows="7" placeholder="-, #ffb454, transparent&#10;#66d9ef, -, #a78bfa&#10;oder JSON { &quot;width&quot;: 16, &quot;height&quot;: 16, &quot;pixels&quot;: [...] }"></textarea>
      <div class="field-hint">Unterstützt Pixelwerkstatt-JSON, Legacy-JSON ({width, height, pixels}) und Hex-Rasterzeilen.</div>
    `;

    // Error / validation message container
    const errorContainer = document.createElement("div");
    errorContainer.className = "modal-feedback error-feedback";
    errorContainer.style.display = "none";

    // Success / info message container
    const infoContainer = document.createElement("div");
    infoContainer.className = "modal-feedback info-feedback";
    infoContainer.style.display = "none";

    body.appendChild(fileGroup);
    body.appendChild(textGroup);
    body.appendChild(errorContainer);
    body.appendChild(infoContainer);

    // Footer buttons
    const footer = document.createElement("div");
    footer.className = "modal-footer";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "button button-ghost";
    cancelBtn.textContent = "Abbrechen";
    cancelBtn.addEventListener("click", () => this.close());

    const submitBtn = document.createElement("button");
    submitBtn.type = "button";
    submitBtn.className = "button button-primary";
    submitBtn.textContent = "Importieren";

    footer.appendChild(cancelBtn);
    footer.appendChild(submitBtn);

    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    this.overlay.appendChild(modal);
    document.body.appendChild(this.overlay);

    const textarea = textGroup.querySelector<HTMLTextAreaElement>("#import-textarea")!;
    const fileInput = fileGroup.querySelector<HTMLInputElement>(".file-input")!;
    const dropzoneLabel = fileGroup.querySelector<HTMLElement>(".dropzone-label")!;
    const closeBtn = header.querySelector<HTMLButtonElement>(".modal-close")!;

    closeBtn.addEventListener("click", () => this.close());
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });

    const showError = (msg: string) => {
      errorContainer.textContent = msg;
      errorContainer.style.display = "block";
      infoContainer.style.display = "none";
    };

    const showInfo = (msg: string) => {
      infoContainer.textContent = msg;
      infoContainer.style.display = "block";
      errorContainer.style.display = "none";
    };

    const clearFeedback = () => {
      errorContainer.textContent = "";
      errorContainer.style.display = "none";
      infoContainer.textContent = "";
      infoContainer.style.display = "none";
    };

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      // Preflight check: reject files larger than MAX_TEXT_LENGTH before FileReader
      if (file.size > MAX_TEXT_LENGTH) {
        fileInput.value = "";
        dropzoneLabel.textContent = "Hier klicken oder Datei ablegen";
        showError(
          `Die Datei ist zu groß (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximal erlaubt sind ${Math.round(MAX_TEXT_LENGTH / (1024 * 1024))} MB.`,
        );
        return;
      }

      dropzoneLabel.textContent = `Ausgewählt: ${file.name} (${Math.round(file.size / 1024)} KB)`;
      const reader = new FileReader();
      reader.onload = () => {
        const content = typeof reader.result === "string" ? reader.result : "";
        if (content.length > MAX_TEXT_LENGTH) {
          showError(
            `Der Dateiinhalt ist zu lang (${(content.length / (1024 * 1024)).toFixed(2)} MB). Maximal erlaubt sind ${Math.round(MAX_TEXT_LENGTH / (1024 * 1024))} MB.`,
          );
          return;
        }
        textarea.value = content;
        try {
          const doc = parseAnyText(content);
          showInfo(`Gültiges Format erkannt: ${doc.width} × ${doc.height} Pixel.`);
        } catch (err) {
          const pError = toPixelwerkstattError(err);
          showError(pError.message);
        }
      };
      reader.onerror = () => {
        showError("Fehler beim Lesen der ausgewählten Datei.");
      };
      reader.readAsText(file);
    });

    textarea.addEventListener("input", () => {
      clearFeedback();
      if (textarea.value.length > MAX_TEXT_LENGTH) {
        showError(
          `Der Text ist zu lang (${(textarea.value.length / (1024 * 1024)).toFixed(2)} MB). Maximal erlaubt sind ${Math.round(MAX_TEXT_LENGTH / (1024 * 1024))} MB.`,
        );
      }
    });

    submitBtn.addEventListener("click", () => {
      // Preflight check on the RAW textarea content: reject oversized input
      // before any trim work. Mirrors the core MAX_TEXT_LENGTH guard.
      const raw = textarea.value;
      if (raw.length > MAX_TEXT_LENGTH) {
        showError(
          `Der Text ist zu lang (${(raw.length / (1024 * 1024)).toFixed(2)} MB). Maximal erlaubt sind ${Math.round(MAX_TEXT_LENGTH / (1024 * 1024))} MB.`,
        );
        return;
      }

      const text = raw.trim();
      if (!text) {
        showError("Bitte zuerst Hex-Raster oder JSON einfügen bzw. eine Datei auswählen.");
        return;
      }

      try {
        const doc = parseAnyText(text);
        if (
          this.callbacks.isDocumentDirty() &&
          !window.confirm("Nicht gespeicherte Änderungen verwerfen und Dokument importieren?")
        ) {
          return;
        }
        this.callbacks.onImportSuccess(doc);
        this.close();
      } catch (err) {
        const pError = toPixelwerkstattError(err);
        showError(pError.message);
      }
    });

    textarea.focus();
  }

  public close(): void {
    if (this.overlay) {
      document.body.removeChild(this.overlay);
      this.overlay = null;
    }
  }
}
